import { useId, useMemo, useState } from 'react';
import i18n from 'helpers/i18n';
import messages from './messages';
import { clsx } from 'clsx';
import { useMediaQuery } from 'react-responsive';
import { useMeasure } from 'context/measure';
import { Button } from '@teachstonellc/component-library';
import { abbrToUppercase, indicatorScoreToRange } from 'helpers/dimension';

import {
  ArrowsExpand,
  ArrrowsMinimize,
  ChevronDown,
  ChevronRight
} from '@teachstonellc/component-library/dist/icons';

import { getDomainColors } from 'components/Measure/ObservationScores/domainColors';

import {
  CycleColumn,
  DimensionRow,
  DomainGroup,
  formatDimensionScore,
  getAllDimensionIds,
  getCycleColumns,
  getPartiallyScoredDimensionIds,
  getScoresByDomain
} from './utils';

/** Sticky so cycle columns scroll under it on small screens. */
const labelCellClasses = 'sticky left-0 z-[1] text-left font-normal';

/**
 * Fixed width, so cycle columns absorb the space left over. Widens at `sm`
 * on the full-width Summary page table -- but a `compact` embed (e.g. beside
 * a textarea, sharing half a page) can still be narrower than that even at
 * `sm` and up, which left the label alone covering the whole visible
 * scrollport and the cycle columns permanently hidden behind it, however far
 * you scrolled. `compact` keeps it at the narrow width unconditionally.
 */
const LABEL_WIDTH_CLASSES = 'w-[175px] overflow-hidden sm:w-[300px]';
const LABEL_WIDTH_CLASSES_COMPACT = 'w-[175px] overflow-hidden';

/** Matches `LABEL_WIDTH_CLASSES`'s own width at each breakpoint. */
const LABEL_WIDTH_BELOW_SM = 175;
const LABEL_WIDTH_AT_SM = 300;

/** Narrowest a cycle column gets before the table starts scrolling. */
const MIN_CYCLE_WIDTH = 72;

const scoreCellClasses =
  'text-center text-ds-xs font-normal text-ds-text-secondary whitespace-nowrap';

/** `compact` tightens this to `8px 4px`, only for the embedded table. */
const SCORE_CELL_PADDING = 'px-2 py-2.5';
const SCORE_CELL_PADDING_COMPACT = 'px-1 py-2';

const rowBorderClasses = 'border-t-[0.5px] border-ds-border';

/** Opaque: the sticky label must not let scrolled cells show through. */
const rowHoverClasses = 'group-hover:bg-ds-gray-2';

interface ScoreCellProps {
  /** Formatted score, or `null` when the score has not been entered. */
  value: string | null;

  /** See `ScoresByCycleTableViewProps.compact`. */
  compact: boolean;

  className?: string;
}

function ScoreCell({ value, compact, className }: ScoreCellProps) {
  return (
    <td
      className={clsx(
        scoreCellClasses,
        compact ? SCORE_CELL_PADDING_COMPACT : SCORE_CELL_PADDING,
        className
      )}
    >
      {value === null ? (
        <>
          <span aria-hidden>-</span>
          <span className="sr-only">{i18n.ft(messages.notScored)}</span>
        </>
      ) : (
        value
      )}
    </td>
  );
}

interface DimensionRowsProps {
  dimension: DimensionRow;
  isInformal: boolean;
  isExpanded: boolean;
  onToggle: (dimensionId: number) => void;

  /** Prefix used to build the ids `aria-controls` points at. */
  rowIdPrefix: string;

  /** See `LABEL_WIDTH_CLASSES`/`LABEL_WIDTH_CLASSES_COMPACT`. */
  labelWidthClasses: string;

  /** See `ScoresByCycleTableViewProps.compact`. */
  compact: boolean;
}

function DimensionRows({
  dimension,
  isInformal,
  isExpanded,
  onToggle,
  rowIdPrefix,
  labelWidthClasses,
  compact
}: DimensionRowsProps) {
  const hasIndicators = dimension.indicators.length > 0;
  const Chevron = isExpanded ? ChevronDown : ChevronRight;

  const indicatorRowId = (indicatorId: number) =>
    `${rowIdPrefix}-dimension-${dimension.id}-indicator-${indicatorId}`;

  /**
   * Abbreviation and name stack below `sm`, where the column is 175px, and
   * sit side by side at `sm` and up, where it's 300px -- except `compact`,
   * which stays at 175px the whole way, so the name always gets its own
   * line rather than the two fighting over a still-narrow row.
   */
  const label = (
    <span
      className={clsx(
        'flex min-w-0 flex-col',
        !compact && 'sm:flex-row sm:items-center sm:gap-1.5'
      )}
    >
      <span className="shrink-0 text-ds-xs font-medium text-ds-text-primary">
        {abbrToUppercase(dimension.abbreviation)}
      </span>
      <span className="min-w-0 truncate text-ds-tiny text-ds-text-hint">
        {dimension.name}
      </span>
    </span>
  );

  return (
    <>
      {/* Click sits on the row; the button's own click bubbles up to it. */}
      <tr
        className={clsx(
          rowBorderClasses,
          hasIndicators && 'group cursor-pointer',
          // sets this row apart from the indicator rows it opens
          hasIndicators && isExpanded && 'border-b-[1.5px]'
        )}
        onClick={hasIndicators ? () => onToggle(dimension.id) : undefined}
      >
        <th
          scope="row"
          className={clsx(
            labelCellClasses,
            'h-10 bg-ds-surface-card',
            rowHoverClasses
          )}
        >
          {hasIndicators ? (
            <button
              type="button"
              aria-expanded={isExpanded}
              aria-controls={dimension.indicators
                .map(indicator => indicatorRowId(indicator.id))
                .join(' ')}
              className={clsx(
                labelWidthClasses,
                'flex items-center gap-1.5 text-left',
                compact ? 'px-1.5 py-2' : 'px-3 py-2.5'
              )}
            >
              <Chevron
                size={14}
                aria-hidden
                className="shrink-0 text-ds-text-secondary"
              />
              {label}
            </button>
          ) : (
            <div
              className={clsx(
                labelWidthClasses,
                'flex items-center gap-1.5 pl-8',
                compact ? 'py-2 pr-1.5' : 'py-2.5 pr-3'
              )}
            >
              {label}
            </div>
          )}
        </th>

        {dimension.scores.map((score, idx) => (
          <ScoreCell
            key={idx}
            value={formatDimensionScore(score, isInformal)}
            compact={compact}
            className={clsx('h-10 bg-ds-surface-card', rowHoverClasses)}
          />
        ))}
      </tr>

      {dimension.indicators.map(indicator => (
        <tr
          key={indicator.id}
          id={indicatorRowId(indicator.id)}
          hidden={!isExpanded}
          className={rowBorderClasses}
        >
          <th
            scope="row"
            className={clsx(labelCellClasses, 'h-9 bg-ds-surface-subtle')}
          >
            <div
              className={clsx(
                labelWidthClasses,
                'py-2 pl-8 text-[13px] leading-[18px] text-ds-text-secondary',
                compact ? 'pr-1.5' : 'pr-3'
              )}
            >
              {indicator.name}
            </div>
          </th>

          {indicator.scores.map((score, idx) => (
            <ScoreCell
              key={idx}
              value={indicatorScoreToRange(score)}
              compact={compact}
              className="h-9 bg-ds-surface-subtle"
            />
          ))}
        </tr>
      ))}
    </>
  );
}

interface DomainRowsProps {
  domain: DomainGroup;
  cycles: CycleColumn[];
  isInformal: boolean;
  expandedDimensions: Set<number>;
  onToggle: (dimensionId: number) => void;
  rowIdPrefix: string;

  /** Hidden for a compact, single-domain embed: the domain is already named elsewhere. */
  showDomainHeader: boolean;

  /** See `LABEL_WIDTH_CLASSES`/`LABEL_WIDTH_CLASSES_COMPACT`. */
  labelWidthClasses: string;

  /** See `ScoresByCycleTableViewProps.compact`. */
  compact: boolean;
}

function DomainRows({
  domain,
  cycles,
  isInformal,
  expandedDimensions,
  onToggle,
  rowIdPrefix,
  showDomainHeader,
  labelWidthClasses,
  compact
}: DomainRowsProps) {
  const color = getDomainColors(domain.abbreviation);

  return (
    <>
      {showDomainHeader && (
        <tr className={rowBorderClasses}>
          <th
            scope="colgroup"
            colSpan={cycles.length + 1}
            className={clsx(
              'h-8 py-2 text-left text-ds-tiny font-medium',
              color.background,
              color.text
            )}
          >
            {/*
              Pinned while the cycle columns scroll. Must shrink to its text: a
              sticky element as wide as its container has no room to slide.
            */}
            <span className="sticky left-0 inline-block px-3">
              {domain.name}
            </span>
          </th>
        </tr>
      )}

      {domain.dimensions.map(dimension => (
        <DimensionRows
          key={dimension.id}
          dimension={dimension}
          isInformal={isInformal}
          isExpanded={expandedDimensions.has(dimension.id)}
          onToggle={onToggle}
          rowIdPrefix={rowIdPrefix}
          labelWidthClasses={labelWidthClasses}
          compact={compact}
        />
      ))}
    </>
  );
}

export interface ScoresByCycleTableViewProps {
  domains: DomainGroup[];
  cycles: CycleColumn[];
  isInformal: boolean;

  /** Hides the "Expand all"/"Collapse all" controls, for compact embeds. */
  showControls?: boolean;

  /** Hides each domain's header row, for a compact, single-domain embed. */
  showDomainHeaders?: boolean;

  /**
   * Keeps the label column at its narrow width regardless of viewport size,
   * for an embed that can be narrower than the label's wider `sm` breakpoint
   * even on a wide screen (e.g. sharing half a page with a textarea) -- see
   * `LABEL_WIDTH_CLASSES_COMPACT`.
   */
  compact?: boolean;
}

/**
 * Presentational scores-by-cycle table. Takes its data as props so it can be
 * reused both for the full catalog (`ScoresByCycleTable`, below) and for a
 * scoped subset of domains, e.g. a single dimension or domain's feedback.
 */
export function ScoresByCycleTableView({
  domains,
  cycles,
  isInformal,
  showControls = true,
  showDomainHeaders = true,
  compact = false
}: ScoresByCycleTableViewProps) {
  const rowIdPrefix = useId();

  // Matches the label column's own actual rendered width, so the table's
  // `min-width` reflects it correctly -- `compact` pins it to the narrow
  // width; otherwise it follows the same `sm` breakpoint the CSS does.
  const isAtLeastSm = useMediaQuery({ minWidth: 640 });
  const labelColumnWidth =
    !compact && isAtLeastSm ? LABEL_WIDTH_AT_SM : LABEL_WIDTH_BELOW_SM;
  const labelWidthClasses = compact
    ? LABEL_WIDTH_CLASSES_COMPACT
    : LABEL_WIDTH_CLASSES;

  /** Seeded on mount only; from then on the expanded set is the reader's. */
  const [expandedDimensions, setExpandedDimensions] = useState<Set<number>>(
    () => new Set(getPartiallyScoredDimensionIds(domains))
  );

  function toggleDimension(dimensionId: number) {
    setExpandedDimensions(expanded => {
      const next = new Set(expanded);

      if (next.has(dimensionId)) {
        next.delete(dimensionId);
      } else {
        next.add(dimensionId);
      }

      return next;
    });
  }

  function expandAll() {
    setExpandedDimensions(new Set(getAllDimensionIds(domains)));
  }

  function collapseAll() {
    setExpandedDimensions(new Set());
  }

  return (
    <div>
      {showControls && (
        <div className="mb-3 flex flex-wrap justify-end gap-4">
          <Button
            variant="secondary"
            shape="rounded"
            size="small"
            endIcon={ArrowsExpand}
            onClick={expandAll}
          >
            {i18n.ft(messages.expandAll)}
          </Button>

          <Button
            variant="secondary"
            shape="rounded"
            size="small"
            endIcon={ArrrowsMinimize}
            onClick={collapseAll}
          >
            {i18n.ft(messages.collapseAll)}
          </Button>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border-[0.5px] border-ds-border bg-ds-surface-card font-inter">
        <table
          className="w-full table-fixed border-collapse"
          style={{
            minWidth: labelColumnWidth + cycles.length * MIN_CYCLE_WIDTH
          }}
        >
          <thead>
            <tr>
              <th
                scope="col"
                className={clsx(
                  labelCellClasses,
                  labelWidthClasses,
                  'h-9 border-b-[0.5px] border-ds-border bg-ds-surface-subtle text-[13px] font-medium leading-[18px] text-ds-text-secondary'
                )}
              >
                <div className={clsx(labelWidthClasses, 'px-3 py-2')}>
                  {i18n.ft(messages.dimension)}
                </div>
              </th>

              {cycles.map(cycle => (
                <th
                  key={cycle.id}
                  scope="col"
                  className={clsx(
                    scoreCellClasses,
                    'h-9 border-b-[0.5px] border-ds-border bg-ds-surface-subtle px-2 py-2 text-[13px] font-medium leading-[18px]'
                  )}
                >
                  <span aria-hidden>
                    {i18n.ft(messages.cycleAbbreviated, {
                      number: cycle.cycleNumber
                    })}
                  </span>
                  <span className="sr-only">
                    {i18n.ft(messages.cycle, { number: cycle.cycleNumber })}
                  </span>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {domains.map(domain => (
              <DomainRows
                key={domain.id}
                domain={domain}
                cycles={cycles}
                isInformal={isInformal}
                expandedDimensions={expandedDimensions}
                onToggle={toggleDimension}
                rowIdPrefix={rowIdPrefix}
                showDomainHeader={showDomainHeaders}
                labelWidthClasses={labelWidthClasses}
                compact={compact}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ScoresByCycleTable() {
  const { assessment, ageLevel } = useMeasure();

  const cycles = useMemo(() => getCycleColumns(assessment), [assessment]);
  const domains = useMemo(
    () => getScoresByDomain(ageLevel, assessment),
    [ageLevel, assessment]
  );

  return (
    <ScoresByCycleTableView
      domains={domains}
      cycles={cycles}
      isInformal={assessment.informal}
    />
  );
}

export default ScoresByCycleTable;
